import {
    useRepayLoanTransaction,
    useAbfFetches,
    AbfLoanExpanded,
    useLoopscaleWalletContext,
    getZcLoanCollateral,
    LoanCollateralExpanded,
    isVaultLpCollateral,
    getVaultLpStakeUnstakeIxParams
} from "@bridgesplit/abf-react";
import { LOADING_ERROR, MISSING_WALLET_ERROR, Result, uiAmountToLamports } from "@bridgesplit/utils";
import { TrackTransactionEvent } from "app/types";
import { RepayLoanTxnParams, WithdrawCollateralParams } from "@bridgesplit/abf-sdk";
import { trackSubmitRepay } from "app/hooks";
import { SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";
import { getCpiFromSwapStep, getPrimarySwapInstructionStep, RouteInstructionStep } from "app/utils/swapInstructions";

import { useTransactionSender } from "../../../transactions";
import { MakeRepaymentParams } from "./types";

function withdrawalUiForVaultLp(
    collateral: LoanCollateralExpanded,
    withdrawalParams: readonly WithdrawCollateralParams[] | undefined
): number {
    if (!withdrawalParams?.length) return 0;
    const forMint = withdrawalParams.find((p) => p.collateralMint === collateral.assetMint);
    if (forMint) return forMint.amount;
    return withdrawalParams[0].amount;
}

function vaultLpUnstakeLamports(
    collateral: LoanCollateralExpanded,
    isFullRepay: boolean,
    withdrawalParams: readonly WithdrawCollateralParams[] | undefined
): { amountLp: number | undefined; skip: boolean } {
    if (!isVaultLpCollateral(collateral.metadata) || !collateral.amount) {
        return { amountLp: undefined, skip: true };
    }

    if (isFullRepay) {
        return { amountLp: uiAmountToLamports(collateral.amount, collateral.metadata.decimals), skip: false };
    }

    const w = Math.min(withdrawalUiForVaultLp(collateral, withdrawalParams), collateral.amount);
    if (w > 0) {
        return {
            amountLp: uiAmountToLamports(w, collateral.metadata.decimals),
            skip: false
        };
    }

    return { amountLp: undefined, skip: true };
}

export function useRepay(props: MakeRepaymentParams) {
    const {
        loanExpanded,
        principalPaymentAmount,
        isFullRepay,
        collateralWithdrawalParams,
        cpiIxs,
        cpiLuts,
        cpiSigners,
        setupIxs,
        setupLuts,
        setupSigners,
        mintToReceive,
        isLoopUnwind
    } = props;
    const send = useTransactionSender();
    const { resetLoanApi, resetMarketsPublicApi, resetLoopApi, resetLendingVaultPositionsApi } = useAbfFetches();

    function resetRepay() {
        resetLoanApi(); // reset loan state
        resetMarketsPublicApi(); // reset orderbook quotes to include repaid amount
        resetLoopApi(); // reset loop state
        resetLendingVaultPositionsApi();
    }

    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const repay = useRepayLoanTransaction(isLoopUnwind);
    const collateral = getZcLoanCollateral(loanExpanded);

    async function repayLoan() {
        if (!loanExpanded) return Result.errFromMessage(LOADING_ERROR);
        if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);
        if (!collateral) return Result.errFromMessage(LOADING_ERROR);
        if (!mintToReceive) return Result.errFromMessage(LOADING_ERROR);
        if (!collateralWithdrawalParams) return Result.errFromMessage(LOADING_ERROR);

        const { amountLp, skip: skipVaultLpUnstake } = vaultLpUnstakeLamports(
            collateral,
            isFullRepay,
            collateralWithdrawalParams
        );

        const params: RepayLoanTxnParams = {
            loan: loanExpanded.loan.address,
            repayParams: [
                {
                    amount: uiAmountToLamports(principalPaymentAmount, loanExpanded.principalMetadata.decimals),
                    ledgerIndex: 0, // TODO: update when multiple ledgers are supported
                    repayAll: isFullRepay
                }
            ],
            collateralWithdrawalParams: collateralWithdrawalParams?.map((param) => ({
                ...param,
                amount: uiAmountToLamports(param.amount, collateral.metadata.decimals)
            })),
            cpiIxs,
            cpiLuts,
            cpiSigners,
            setupIxs,
            setupLuts,
            setupSigners,
            unifySetup: false,
            mintToReceive,
            userPublicKey: activeWallet,
            stakeInstructions: getVaultLpStakeUnstakeIxParams({
                vault: collateral?.oracleAccount,
                amountLp,
                skip: skipVaultLpUnstake
            })
        };

        return await send(repay, params, {
            description: "Repaying loan",
            sendOptions: { refetch: () => resetRepay() },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitRepay,
                params: trackSubmitRepay(params, loanExpanded.principalMetadata) // TODO CHECK IF ARRAY IS ACCEPTABLE
            }
        });
    }

    return {
        repay: () => repayLoan()
    };
}

export function createRepaymentParams({
    principalPaymentUiAmount,
    collateralPaymentUiAmount,
    isFullRepay,
    repayType,
    collateral,
    loanExpanded,
    quote,
    isLoopUnwind,
    instructionStep
}: {
    principalPaymentUiAmount: number | undefined; // ui amount
    collateralPaymentUiAmount: number | undefined; // ui amount
    isFullRepay: boolean;
    repayType: WindType;
    collateral: LoanCollateralExpanded | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    quote: SwapInstructionsResponse | undefined;
    isLoopUnwind?: boolean;
    instructionStep?: RouteInstructionStep;
}): MakeRepaymentParams {
    const swapInstructions = instructionStep ?? getPrimarySwapInstructionStep(quote);
    // Keep setupIxs separate so downstream can forward them into RepayLoanTxnParams.setupIxs
    // (with unifySetup: false) and BE emits a separate setup tx instead of inflating the main.
    const { cpiIxs, cpiLuts, cpiSigners, setupIxs, setupSigners } = getCpiFromSwapStep(swapInstructions, {
        flattenSetup: false
    });

    const receiveToken =
        repayType === WindType.Collateral ? collateral?.assetMint : loanExpanded?.principalMetadata.mint;

    return {
        loanExpanded,
        principalPaymentAmount: principalPaymentUiAmount, // ui amount
        isFullRepay,
        cpiIxs,
        cpiLuts,
        cpiSigners,
        setupIxs,
        setupLuts: cpiLuts,
        setupSigners,
        collateralWithdrawalParams: getCollateralWithdrawalParams({
            collateral,
            isFullRepay,
            collateralPaymentUiAmount,
            repayType,
            isLoopUnwind
        }),
        mintToReceive: receiveToken,
        isLoopUnwind: isLoopUnwind ?? false
    };
}

function getCollateralWithdrawalParams({
    collateral,
    isFullRepay,
    collateralPaymentUiAmount,
    isLoopUnwind,
    repayType
}: {
    collateral: LoanCollateralExpanded | undefined;
    isFullRepay: boolean;
    collateralPaymentUiAmount: number | undefined;
    repayType: WindType;
    isLoopUnwind: boolean | undefined;
}): WithdrawCollateralParams[] | undefined {
    if (!collateral) return undefined;

    const includeCollateralWithdrawal = repayType === WindType.Collateral || isLoopUnwind || isFullRepay;

    if (!includeCollateralWithdrawal) return [];

    return [
        {
            collateralMint: collateral.assetMint,
            amount: isFullRepay ? collateral.amount : collateralPaymentUiAmount ?? 0,
            closeIfEligible: isFullRepay
        }
    ];
}
